import { useCallback } from "react";
import { useAuth } from "react-oidc-context";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const DEFAULT_TIMEOUT = 600000; // 10 minutes in milliseconds
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

export function useApi() {
  const auth = useAuth();

  const getAuthHeaders = useCallback(() => {
    if (!auth.isAuthenticated) {
      throw new Error("Not authenticated");
    }
    const token = auth.user?.access_token;
    return {
      Authorization: `Bearer ${token}`,
    };
  }, [auth.isAuthenticated, auth.user?.access_token]);

  // Helper to create fetch with timeout and automatic retry on network errors
  const fetchWithTimeout = useCallback(async (url, options = {}, timeout = DEFAULT_TIMEOUT) => {
    let lastError;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        return response;
      } catch (err) {
        clearTimeout(timeoutId);
        lastError = err;
        // Only retry on network errors, not on abort/timeout
        if (err.name === 'AbortError' || attempt === MAX_RETRIES) throw err;
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * (attempt + 1)));
      }
    }
    throw lastError;
  }, []);

  // Generic fetch wrapper for GET requests
  const fetchGet = useCallback(async (endpoint, options = {}) => {
    const headers = getAuthHeaders();
    const timeout = options.timeout || DEFAULT_TIMEOUT;
    const response = await fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        ...headers,
        ...options.headers,
      },
      ...options,
    }, timeout);
    return response;
  }, [fetchWithTimeout, getAuthHeaders]);

  // Generic fetch wrapper for POST requests with FormData
  const fetchPost = useCallback(async (endpoint, body, options = {}) => {
    const headers = getAuthHeaders();
    const timeout = options.timeout || DEFAULT_TIMEOUT;
    const response = await fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        ...headers,
        ...options.headers,
      },
      body,
      ...options,
    }, timeout);
    return response;
  }, [fetchWithTimeout, getAuthHeaders]);

  // Generic fetch wrapper for POST requests with JSON body
  const fetchPostJson = useCallback(async (endpoint, data, options = {}) => {
    const headers = getAuthHeaders();
    const timeout = options.timeout || DEFAULT_TIMEOUT;
    const response = await fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body: JSON.stringify(data),
      ...options,
    }, timeout);
    return response;
  }, [fetchWithTimeout, getAuthHeaders]);

  const fetchDelete = useCallback(async (endpoint, options = {}) => {
    const headers = getAuthHeaders();
    const timeout = options.timeout || DEFAULT_TIMEOUT;
    const response = await fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: {
        ...headers,
        ...options.headers,
      },
      ...options,
    }, timeout);
    return response;
  }, [fetchWithTimeout, getAuthHeaders]);

  // Axios-style post for JSON data (used by OrderBuilder)
  const axiosPost = useCallback(async (endpoint, data, options = {}) => {
    const headers = getAuthHeaders();
    const timeout = options.timeout || DEFAULT_TIMEOUT;
    const response = await fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }, timeout);

    const responseData = await response.json();

    // Return axios-like response object
    return {
      data: responseData,
      status: response.status,
      ok: response.ok,
    };
  }, [fetchWithTimeout, getAuthHeaders]);

  return {
    fetchGet,
    fetchPost,
    fetchPostJson,
    fetchDelete,
    axiosPost,
    isAuthenticated: auth.isAuthenticated,
    API_BASE_URL,
  };
}

export default useApi;
